import {Component, input, output} from '@angular/core';

/**
 * One of the four summary cards on the event-details page (Verkaufspunkt / Sonstiges / Transaktionstypen /
 * Artikel): a fixed header with an icon + label and a scrollable definition list underneath.
 *
 * The selector is an **attribute on the `<li>`** so the card stays a direct grid child of the page's `<ul>`;
 * a wrapper element would break the grid.
 *
 * **Content slots.** `[card-icon]` projects the leading icon; `[card-actions]` (#534) projects right-aligned
 * header controls — today the "Anzeigeoptionen" gear on the "Artikel" card, whose panel holds the
 * "Kombinationen einzeln" switch. The actions wrapper stops click propagation, so a projected control can
 * never trigger the card's own header behaviour. Anything else projected lands in the scrollable `<dl>` body.
 *
 * **{@link settling}** (#716 / Task #722 follow-up) marks the ROWS — never the header label — as "not the final
 * answer yet". See the input's own documentation for why it is scoped that narrowly.
 *
 * Layout notes (Bug #231) — the four cards used to carry a hand-copied version of this markup that was wrong
 * in two compounding ways:
 *
 * 1. `h-50` on the list and `h-100` on the `<dl>` are **Bootstrap** utilities (`height: 50%/100% !important`,
 *    from the globally loaded `bootstrap.min.css`), not Tailwind ones. `h-100` therefore sized the scroll
 *    area to the FULL card height — header included — so its last ~68px hung below the card and were clipped
 *    by the card's own `overflow-hidden`.
 * 2. A forced `!pb-32 lg:!pb-20` (128px / 80px) inside the scroll area compensated for that clipped strip.
 *    It is what the user saw as a large empty gap under the last entry, next to a second scrollbar.
 *
 * The fix is ordinary flex: the card is a column, the header sizes itself, and the list takes the rest
 * (`flex-1 min-h-0`) up to a cap it can actually scroll within. No percentage heights, no `50vh`, no padding
 * standing in for a height.
 */
@Component({
  selector: 'li[app-event-summary-card]',
  templateUrl: './event-summary-card.component.html',
  host: {
    // A flex COLUMN: the header keeps its intrinsic height and the list below it gets the remainder.
    // `lg:!flex` carries `!important`, so it beats the `hidden` toggle — below `lg` one card is shown at a
    // time (cycled with the swap button), at `lg+` all four are laid out at once.
    //
    // NO `overflow-hidden` here (#534). It used to clip the header's grey background to the card's rounded
    // corners, but an ancestor's `overflow-hidden` clips EVERY descendant — including the anchored
    // "Anzeigeoptionen" panel projected into `[card-actions]`, which opens downward and is wider than the
    // gear. The header now rounds its own top corners instead (`rounded-t-xl` in the template), which is the
    // only thing the clipping was actually doing: the `<dl>` body has no background of its own and scrolls
    // within itself.
    'class': 'flex flex-col rounded-xl border border-gray-200 shadow-lg lg:!flex',
    '[class.hidden]': 'collapsed()'
  }
})
export class EventSummaryCardComponent {
  /** Header text, e.g. `Verkaufspunkt: 1.234,00 €`. */
  readonly label = input.required<string>();

  /** Hides the card on small screens while another one of the four is being shown. */
  readonly collapsed = input(false);

  /**
   * The rows in the `<dl>` body are a TRANSIENT answer that a fresh one is about to replace (#716 / Task #722).
   *
   * Renders exactly two things: a 2px indeterminate line on the header's bottom edge — directly above the rows
   * it describes — and `aria-busy` on the list. No spinner, no overlay, no dimming of the rows (see the
   * template for why dimming was tried and dropped): the numbers stay fully legible, and flipping a saved
   * display preference must not read as a data operation.
   *
   * **The header label is deliberately untouched**, and so are the page's hero tiles: on the event-details page
   * they come from the server's event-wide aggregate and are exact throughout. Only the per-article rows are
   * briefly folded from the loaded page, so only they are marked.
   *
   * The line has no layout footprint (it is absolutely positioned inside the header), so a 360px card header
   * lays out exactly as it does at rest — the alternative, a "wird aktualisiert" pill beside the label, would
   * have squeezed the label, the gear and the swap button on a phone for a state that lasts one round trip.
   */
  readonly settling = input(false);

  /** Raised by the swap button (phones/tablets only) to advance to the next card. */
  readonly next = output<void>();
}
